import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import customerService from '../../services/customerService';

/**
 * Navigation to a host's shops, for public pages.
 *
 * Shown on the shop pages themselves and on booking pages that need a plan or
 * points — the places where "where do I buy that?" is a live question and the
 * answer was previously nowhere.
 *
 * ONE SHOP MEANS A PLAIN LINK. A dropdown that opens to reveal a single choice
 * is a click spent on nothing, so with only plans (or only points) this renders
 * a direct link to that page and skips the menu entirely. Two shops get the
 * menu. No shops renders nothing at all — a host who sells neither should not
 * have an empty control in their header.
 *
 * The counts come from `GET /api/customer/shops/:username`, so a link is only
 * ever offered to a page that has something on it.
 */

interface ShopNavProps {
  username: string;
  /** Highlighted when the viewer is already on that shop. */
  current?: 'plans' | 'points';
  className?: string;
}

const ShopNav: React.FC<ShopNavProps> = ({ username, current, className = '' }) => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<{ plans: number; points: number } | null>(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    customerService
      .getShopSummary(username)
      .then((r) => {
        if (!cancelled) setSummary(r);
      })
      // Silent: navigation that could not load is navigation that is not shown,
      // which is better than an error in someone's booking header.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [username]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!summary) return null;

  const entries = [
    summary.plans > 0 && {
      key: 'plans' as const,
      label: t('shop:navPlans', 'Plans'),
      href: `/${username}/shop/plans`,
    },
    summary.points > 0 && {
      key: 'points' as const,
      label: t('shop:navPoints', 'Points'),
      href: `/${username}/shop/points`,
    },
  ].filter(Boolean) as Array<{ key: 'plans' | 'points'; label: string; href: string }>;

  if (entries.length === 0) return null;

  if (entries.length === 1) {
    const only = entries[0];
    return (
      <a
        href={only.href}
        className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-colors ${
          current === only.key
            ? 'bg-gray-100 font-medium text-gray-900'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        } ${className}`}
      >
        {only.label}
      </a>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
      >
        {t('shop:navBuy', 'Buy')}
        <ChevronDownIcon className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-44 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {entries.map((entry) => (
            <a
              key={entry.key}
              href={entry.href}
              role="menuitem"
              className={`block px-4 py-2.5 text-sm transition-colors hover:bg-gray-50 ${
                current === entry.key ? 'font-medium text-gray-900' : 'text-gray-700'
              }`}
            >
              {entry.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShopNav;
